import { DIET_TAGS, normalizeDietTags, type DietTag, type MenuItemDto } from '@restaurant/shared';
import { Check, Sparkles } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/misc';
import { api } from '@/lib/api';
import { toastError } from '@/lib/errors';
import { cn } from '@/lib/utils';

const LABEL: Record<DietTag, string> = {
  vegan: 'Vegan', vegetarian: 'Veg', egg: 'Egg', dairy: 'Dairy', chicken: 'Chicken', beef: 'Beef', pork: 'Pork', mutton: 'Mutton', fish: 'Fish', seafood: 'Seafood',
};
const MEATY = new Set<DietTag>(['chicken', 'beef', 'pork', 'mutton', 'fish', 'seafood']);

/** Diet tags on a menu row. Owners confirm AI suggestions ("Looks right") or fix them. */
export function DietTagsRow({ item, canEdit, onSaved }: { item: MenuItemDto; canEdit: boolean; onSaved: () => void }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<DietTag[]>(item.dietTags);
  const [busy, setBusy] = useState(false);

  const save = async (tags: DietTag[]) => {
    setBusy(true);
    try {
      await api.menu.setDietTags(item.id, normalizeDietTags(tags));
      setEditing(false);
      onSaved();
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };

  if (editing) {
    return (
      <div className="mt-1 flex flex-wrap items-center gap-1">
        {DIET_TAGS.map((t) => (
          <button
            key={t}
            onClick={() => setDraft(draft.includes(t) ? draft.filter((x) => x !== t) : [...draft, t])}
            className={cn('rounded-full border px-2 py-0.5 text-[11px]', draft.includes(t) ? 'border-primary bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-accent')}
          >
            {LABEL[t]}
          </button>
        ))}
        <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={() => void save(draft)} disabled={busy}>{busy ? <Spinner /> : <Check />} Save</Button>
        <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={() => setEditing(false)}>Cancel</Button>
      </div>
    );
  }

  return (
    <div className="mt-0.5 flex flex-wrap items-center gap-1">
      {item.dietTags.length ? (
        item.dietTags.map((t) => (
          <span key={t} className={cn('rounded-full px-1.5 text-[10px]', MEATY.has(t) ? 'bg-destructive/10 text-destructive' : t === 'vegan' || t === 'vegetarian' ? 'bg-success/15 text-success' : 'bg-muted text-muted-foreground')}>
            {LABEL[t]}
          </span>
        ))
      ) : (
        <span className="text-[10px] text-muted-foreground">No diet tags yet</span>
      )}
      {!item.dietTagsReviewed && item.dietTags.length ? (
        <span className="flex items-center gap-0.5 text-[10px] text-primary"><Sparkles className="size-3" /> AI guess</span>
      ) : null}
      {canEdit ? (
        <>
          {!item.dietTagsReviewed && item.dietTags.length ? (
            <button className="text-[10px] underline" onClick={() => void save(item.dietTags).then(() => toast.success(`${item.name}: tags confirmed`))} disabled={busy}>
              Looks right
            </button>
          ) : null}
          <button className="text-[10px] text-muted-foreground underline" onClick={() => { setDraft(item.dietTags); setEditing(true); }}>
            edit
          </button>
        </>
      ) : null}
    </div>
  );
}
